import React from 'react';
import { Button, Card, Flex, Menu, MenuItem, MenuTrigger, Text, TruncatedText, TagBadge } from '@chatsdet/ui';
import {
  DotsThreeVertical,
  DownloadSimple,
  FileCsv,
  FileDoc,
  FilePdf,
  FileXls,
  Trash,
} from '@phosphor-icons/react';
import type { AttachmentListItem } from '@chatsdet/types';
import { attachmentRawUrl } from '../api';
import { formatBytes, getFileTypeBadge, isImage, isCsv, isExcel, isDocx } from '../utils';

type AttachmentCardProps = {
  attachment: AttachmentListItem;
  onDelete?: (id: string) => void;
  isSelected?: boolean;
  onToggleSelected?: (id: string) => void;
};

function getFileIcon(contentType: string) {
  if (isCsv(contentType)) return { icon: FileCsv, label: 'CSV' };
  if (isExcel(contentType)) return { icon: FileXls, label: 'Excel' };
  if (isDocx(contentType)) return { icon: FileDoc, label: 'Word' };
  return { icon: FilePdf, label: 'PDF' };
}

export function AttachmentCard({
  attachment,
  onDelete,
  isSelected,
  onToggleSelected,
}: AttachmentCardProps) {
  const previewUrl = attachmentRawUrl(attachment.id);
  const image = isImage(attachment.contentType);
  const { icon: FileIcon, label: iconLabel } = getFileIcon(attachment.contentType);

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="relative">
        <div className="h-36 bg-muted/40 flex items-center justify-center">
          {image ? (
            <img
              src={previewUrl}
              alt={attachment.filename}
              className="h-36 w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <FileIcon size={28} />
              <Text size="xs">{iconLabel}</Text>
            </div>
          )}
        </div>

        {typeof onToggleSelected === 'function' && (
          <button
            type="button"
            className="absolute top-2 left-2 h-5 w-5 rounded border border-border bg-background/90 shadow-sm"
            aria-label={isSelected ? 'Deselect attachment' : 'Select attachment'}
            onClick={() => onToggleSelected(attachment.id)}
          >
            {isSelected ? <div className="h-full w-full bg-primary" /> : null}
          </button>
        )}

      </div>

      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <TruncatedText text={attachment.filename} lines={2} className="text-sm font-medium text-foreground" />
          <TagBadge name={getFileTypeBadge(attachment.contentType)} color="gray" hideDot size="sm" />
        </div>

        <div className="flex items-center justify-between">
          <Text size="xs" className="text-muted-foreground">
            {formatBytes(attachment.sizeBytes)}
          </Text>
          <div className="flex items-center gap-1">
            {onDelete && (
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Delete attachment"
                aria-label="Delete attachment"
                onPress={() => onDelete(attachment.id)}
              >
                <Trash size={16} />
              </Button>
            )}
            <MenuTrigger>
              <Button variant="icon" size="sm" iconOnly tooltip="Attachment actions">
                <DotsThreeVertical size={16} />
              </Button>
              <Menu>
                <MenuItem
                  id="download"
                  onAction={() => {
                    window.open(previewUrl, '_blank', 'noopener');
                  }}
                >
                  <Flex align="center" gap="sm">
                    <DownloadSimple size={16} />
                    Download
                  </Flex>
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </div>
        </div>
      </div>
    </Card>
  );
}
